import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import { cn } from "@/lib/utils";

// 44px is the target on desktop and touch alike — one spec, no lg: shrink. See design-system.md.
const buttonVariants = cva(
  [
    "inline-flex shrink-0 items-center justify-center gap-2 rounded-[14px] whitespace-nowrap",
    "text-body font-semibold shadow-sm outline-none",
    "transition-transform duration-[var(--duration-fast)] ease-[var(--ease-standard)]",
    "hover:-translate-y-px active:translate-y-0 motion-reduce:transform-none",
    "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-[var(--ring-soft)] focus-visible:outline-none",
    "disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none",
    "aria-invalid:border-destructive",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-[19px]",
  ].join(" "),
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        secondary: "bg-card text-foreground border border-[var(--border-strong)] hover:bg-accent",
        // outline keeps its name for existing callers; it is the same control as secondary.
        outline: "bg-card text-foreground border border-[var(--border-strong)] hover:bg-accent",
        ghost: "shadow-none hover:bg-accent hover:text-accent-foreground",
        sign: "bg-[var(--primary-ink)] text-primary-foreground border border-[var(--seal)]",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        "destructive-soft":
          "bg-destructive/10 text-destructive border border-destructive/30 hover:bg-destructive/15",
        link: "text-primary shadow-none underline-offset-4 hover:translate-y-0 hover:underline",
      },
      size: {
        default: "h-11 px-5",
        // sm is compact across, never short: 44px is the minimum target on desktop too.
        sm: "h-11 gap-1.5 px-3.5 text-body-sm",
        lg: "h-12 px-6",
        icon: "size-11 p-0",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  type,
  ...props
}: React.ComponentProps<"button"> & VariantProps<typeof buttonVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "button";
  return (
    <Comp
      data-slot="button"
      // Default to "button": a bare <Button> inside a <form> must never submit by accident.
      type={asChild ? type : (type ?? "button")}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
